import { cloneElement, ReactElement, type JSX } from 'react';
import { Icon, IconBase, IconProps } from '@doxyme/icons';
import { useTheme } from '../../../../theme';
import { Button, type ButtonProps } from '../button';
import { useButtonWithDropdownStyles } from './buttonWithDropdown.styles';
import { BodyS } from '../../../../typography';
import Box from '@mui/material/Box';
import { getIconSize } from '../button/button';
import { getStylesBySize, getIconMarginBySize } from '../button/button';

export type ButtonWithDropdownProps = Omit<ButtonProps, 'endIcon' | 'width'> & {
  children: string;
  type: 'primary' | 'secondary';
  fullWidth?: boolean;
  dropdownOpen: boolean;
};

export const ButtonWithDropdown = ({
  dropdownOpen,
  fullWidth = false,
  startIcon,
  children,
  size = 'regular',
  status,
  isLoading = false,
  type,
  ...rest
}: ButtonWithDropdownProps): JSX.Element => {
  const theme = useTheme();
  const styles = useButtonWithDropdownStyles({ theme, fullWidth, size });
  const buttonStyles = getStylesBySize({
    size,
    type,
    theme,
    isLoading: isLoading,
  });
  if (Boolean(status) || isLoading) {
    return (
      <Button
        {...rest}
        size={size}
        status={status}
        isLoading={isLoading}
        type={type}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button
      {...rest}
      css={{
        ...styles.overlay,
        ...(fullWidth ? styles.fullButton : {}),
        minWidth: type === 'primary' && 'xsmall' !== size ? 100 : undefined,
        gap: getIconMarginBySize(size),
      }}
      aria-haspopup={true}
      aria-expanded={dropdownOpen}
      size={size}
      type={type}
    >
      <Box css={styles.container}>
        <Box css={styles.textContainer}>
          {Boolean(startIcon) &&
            size !== 'xsmall' &&
            cloneElement(startIcon as ReactElement<IconProps>, {
              size: getIconSize(size),
            })}

          <BodyS
            tag='span'
            fontWeight='medium'
            css={{
              alignItems: 'center',
            }}
          >
            {children}
          </BodyS>
        </Box>

        <Box css={styles.iconContainer}>
          <IconBase
            width={8}
            height={buttonStyles.minHeight - 2}
            viewBox={`0 0 8 ${buttonStyles.minHeight - 2}`}
          >
            <rect
              x='7.25'
              y='1.25'
              width='0.5'
              height='37.5'
              stroke='black'
              strokeOpacity='0.12'
              strokeWidth='0.5'
            />
            <rect
              x='6.25'
              y='1.25'
              width='0.5'
              height='37.5'
              stroke='white'
              strokeOpacity='0.2'
              strokeWidth='0.5'
            />
          </IconBase>

          {dropdownOpen ? (
            <Icon.ChevronUp
              size={size === 'regular' ? 'small' : 'xsmall'}
              role='presentation'
            />
          ) : (
            <Icon.ChevronDown
              size={size === 'regular' ? 'small' : 'xsmall'}
              role='presentation'
            />
          )}
        </Box>
      </Box>
    </Button>
  );
};
